import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import SubjectThumbnail from '@/components/SubjectThumbnail';
import {
  BookOpen, ArrowLeft, ChevronRight, Hash, PlusCircle,
  X, Percent, Ruler, Clock, CheckCircle2, Sparkles,
  Scale, BarChart3, Variable, BookText, Languages,
  FileText, Pencil, Quote, Library, Type, Move,
  Zap, Droplets, Waves, Atom, LogOut, Home,
  Calculator, Beaker, Dna, ChevronDown, Lightbulb,
  HelpCircle, Brain, Layers, Bot, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Maths category icons
const MATHS_CATEGORY_ICONS = {
  'number-place-value': Hash,
  'addition-subtraction': PlusCircle,
  'multiplication-division': X,
  'fractions': Percent,
  'decimals-percentages': Percent,
  'geometry': Ruler,
  'ratio-proportion': Scale,
  'statistics': BarChart3,
  'algebra': Variable,
  // 7+ Maths categories
  'number-counting': Hash,
  'addition-subtraction-7plus': PlusCircle,
  'shape-space-measure': Ruler,
  'money-fractions': Percent
};

// English category icons
const ENGLISH_CATEGORY_ICONS = {
  'vocabulary-development': BookText,
  'grammar-language': Pencil,
  'punctuation': Type,
  'spelling': Languages,
  'reading-comprehension': FileText,
  'creative-writing': Quote,
  // 7+ English categories
  'phonics-spelling-7plus': Languages,
  'grammar-7plus': Pencil,
  'punctuation-7plus': Type,
  'reading-writing-7plus': BookText
};

// Kumon 7+ category icons
const KUMON_CATEGORY_ICONS = {
  'kumon-maths': Calculator,
  'kumon-english': BookOpen
};

// Physics category icons
const PHYSICS_CATEGORY_ICONS = {
  'forces-motion': Move,
  'energy': Zap,
  'pressure-fluids': Droplets,
  'waves': Waves
};

// STEM subject icons
const STEM_SUBJECT_ICONS = {
  maths: Calculator,
  physics: Atom,
  chemistry: Beaker,
  biology: Dna
};

const STEM_SUBJECT_COLORS = {
  maths: { bg: 'from-blue-600 to-indigo-700', border: 'border-blue-500/30', text: 'text-blue-600', badge: 'bg-blue-500/20' },
  physics: { bg: 'from-amber-500 to-orange-600', border: 'border-amber-500/30', text: 'text-amber-600', badge: 'bg-amber-500/20' },
  chemistry: { bg: 'from-emerald-500 to-teal-600', border: 'border-emerald-500/30', text: 'text-emerald-600', badge: 'bg-emerald-500/20' },
  biology: { bg: 'from-pink-500 to-rose-600', border: 'border-pink-500/30', text: 'text-pink-600', badge: 'bg-pink-500/20' }
};

const CATEGORY_ICONS = { ...MATHS_CATEGORY_ICONS, ...ENGLISH_CATEGORY_ICONS, ...PHYSICS_CATEGORY_ICONS, ...KUMON_CATEGORY_ICONS };

const CATEGORY_COLORS = {
  blue: 'from-blue-600 to-indigo-600',
  green: 'from-green-600 to-emerald-600',
  purple: 'from-purple-600 to-violet-600',
  orange: 'from-orange-600 to-amber-600',
  pink: 'from-pink-600 to-rose-600',
  teal: 'from-teal-600 to-cyan-600',
  amber: 'from-amber-600 to-yellow-600',
  cyan: 'from-cyan-600 to-sky-600',
  rose: 'from-rose-600 to-pink-600',
  violet: 'from-violet-600 to-purple-600'
};

const CATEGORY_BG = {
  blue: 'bg-blue-500/20',
  green: 'bg-green-500/20',
  purple: 'bg-purple-500/20',
  orange: 'bg-orange-500/20',
  pink: 'bg-pink-500/20',
  teal: 'bg-teal-500/20',
  amber: 'bg-amber-500/20',
  cyan: 'bg-cyan-500/20',
  rose: 'bg-rose-500/20',
  emerald: 'bg-emerald-500/20',
  violet: 'bg-violet-500/20'
};

// LLM Models for STEM AI
const LLM_MODELS = [
  { id: 'openai', name: 'GPT-4o' },
  { id: 'anthropic', name: 'Claude Sonnet' },
  { id: 'gemini', name: 'Gemini Flash' }
];

const getRandomLLM = () => LLM_MODELS[Math.floor(Math.random() * LLM_MODELS.length)].id;

export const ResourceHub = ({ progress }) => {
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryTopics, setCategoryTopics] = useState(null);
  
  // STEM Library specific state
  const [stemTopics, setStemTopics] = useState(null);
  const [selectedStemSubject, setSelectedStemSubject] = useState('maths');
  const [selectedStemCategory, setSelectedStemCategory] = useState(null);
  const [selectedStemTopic, setSelectedStemTopic] = useState(null);
  const [customTopic, setCustomTopic] = useState('');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentType, setContentType] = useState('description');

  // Get user info to check year group
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userYearGroup = (user.year_group || user.yearGroup || user.studentProfile?.yearGroup || '').toLowerCase();
  const userType = (user.user_type || user.userType || '').toLowerCase();
  
  // Physics is available for Year 8+ students
  const canAccessPhysics = 
    userYearGroup.includes('year 8') || userYearGroup.includes('year8') ||
    userYearGroup.includes('year 9') || userYearGroup.includes('year9') ||
    userYearGroup.includes('year 10') || userYearGroup.includes('year10') ||
    userYearGroup.includes('year 11') || userYearGroup.includes('year11') ||
    userYearGroup.includes('year 12') || userYearGroup.includes('year12') ||
    userYearGroup.includes('year 13') || userYearGroup.includes('year13') ||
    userYearGroup.includes('gcse') || userYearGroup.includes('ks3') || userYearGroup.includes('ks4') ||
    userType.includes('gcse') || userType.includes('a-level') || userType.includes('alevel') ||
    userType.includes('secondary');

  // 7+ Resources available for Year 1 / Year 2 / 7+ students (and always for admins)
  // Use word-boundary regex so "Year 10/11/12/13" is NOT mis-classified as 7+ KS1.
  const ks1YearRegex = /\byear[\s-]?[12]\b/i;
  const is7PlusLearner =
    ks1YearRegex.test(userYearGroup) ||
    userYearGroup.includes('7+') || userYearGroup.includes('7plus') ||
    userYearGroup.includes('ks1') ||
    userType.includes('7+') || userType.includes('7plus') ||
    ks1YearRegex.test(userType);
  const isAdminUser = Boolean(user.isAdmin);
  const canAccess7Plus = is7PlusLearner || isAdminUser;

  // Detect KS3 / GCSE learners (Year 7+) so we can hide irrelevant KS2 cards from them.
  // Use word boundaries so "Year 7" matches but "Year 17" wouldn't, and so on.
  const ks3PlusYearRegex = /\byear[\s-]?(7|8|9|1[0-3])\b/i;
  const isKS3Plus =
    ks3PlusYearRegex.test(userYearGroup) ||
    userYearGroup.includes('ks3') || userYearGroup.includes('ks4') ||
    userYearGroup.includes('gcse') || userYearGroup.includes('a-level') || userYearGroup.includes('alevel') ||
    userType.includes('gcse') || userType.includes('a-level') || userType.includes('alevel') ||
    userType.includes('secondary') || ks3PlusYearRegex.test(userType);

  // Hide KS2 Maths/English cards for pure 7+ learners AND KS3/GCSE/A-Level learners
  // (they're Year 4-6 content, not relevant). Admin always sees them for review.
  const showKS2MainCards = isAdminUser || (!is7PlusLearner && !isKS3Plus);

  // STEM Library is available for Year 9-10 and GCSE students only
  const canAccessSTEM = 
    userYearGroup.includes('year 9') || userYearGroup.includes('year9') ||
    userYearGroup.includes('year 10') || userYearGroup.includes('year10') ||
    userYearGroup.includes('year 11') || userYearGroup.includes('year11') ||
    userYearGroup.includes('gcse') || userYearGroup.includes('ks4') ||
    userType.includes('gcse') || userType.includes('year 9') || userType.includes('year 10');

  useEffect(() => {
    fetchProgress();
  }, []);

  useEffect(() => {
    if (selectedSubject && selectedSubject !== 'stem') {
      fetchCategories(selectedSubject);
    } else if (selectedSubject === 'stem') {
      fetchStemTopics();
    }
  }, [selectedSubject]);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const res = await axios.get(`${API}/api/resources/progress/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setUserProgress(res.data);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (subject) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/resources/${subject}/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStemTopics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/stem-library/topics`);
      setStemTopics(res.data);
    } catch (error) {
      console.error('Error fetching STEM topics:', error);
      toast.error('Failed to load STEM topics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTopics = async (categoryId) => {
    try {
      const res = await axios.get(`${API}/api/resources/${selectedSubject}/category/${categoryId}`);
      setCategoryTopics(res.data);
      setSelectedCategory(categoryId);
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  const generateStemContent = async (type) => {
    const topicToUse = selectedStemTopic || customTopic;
    if (!topicToUse) {
      toast.error('Please select or enter a topic');
      return;
    }

    setContentLoading(true);
    setContentType(type);
    
    try {
      const randomLLM = getRandomLLM();
      const response = await axios.post(`${API}/api/stem-library/generate`, {
        subject: selectedStemSubject,
        topic: topicToUse,
        content_type: type,
        llm_provider: randomLLM
      });
      
      setGeneratedContent(response.data.content);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} generated!`);
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setContentLoading(false);
    }
  };

  const getTopicProgress = (categoryId, topicId) => {
    if (!userProgress?.progress) return 0;
    const record = userProgress.progress.find(
      p => p.category_id === categoryId && p.topic_id === topicId
    );
    if (!record) return 0;
    return (record.completed_sections?.length || 0) * 20;
  };

  const getCategoryProgress = (categoryId) => {
    if (!userProgress?.progress) return 0;
    const categoryRecords = userProgress.progress.filter(p => p.category_id === categoryId);
    const category = categories.find(c => c.id === categoryId);
    if (!category || category.topic_count === 0) return 0;
    
    const totalSections = category.topic_count * 5;
    const completedSections = categoryRecords.reduce(
      (sum, r) => sum + (r.completed_sections?.length || 0), 0
    );
    return Math.round((completedSections / totalSections) * 100);
  };

  const handleBackNavigation = () => {
    if (selectedSubject === 'stem') {
      if (generatedContent) {
        setGeneratedContent(null);
      } else if (selectedStemTopic || customTopic) {
        setSelectedStemTopic(null);
        setCustomTopic('');
      } else if (selectedStemCategory) {
        setSelectedStemCategory(null);
      } else {
        setSelectedSubject(null);
        setStemTopics(null);
      }
    } else if (selectedCategory) {
      setSelectedCategory(null);
      setCategoryTopics(null);
    } else if (selectedSubject) {
      setSelectedSubject(null);
      setCategories([]);
    } else {
      navigate(-1);
    }
  };

  if (loading && !selectedSubject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-slate-900">Loading...</div>
      </div>
    );
  }

  const stemSubjectData = stemTopics?.[selectedStemSubject];
  const stemColors = STEM_SUBJECT_COLORS[selectedStemSubject];

  return (
    <div 
      data-testid="resource-hub-page"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackNavigation}
              className="text-slate-900 hover:bg-slate-100"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900">
                {selectedSubject === 'stem' 
                  ? 'STEM Library'
                  : selectedCategory 
                    ? categoryTopics?.category?.name 
                    : selectedSubject 
                      ? `${selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)} Resources`
                      : 'Resources Hub'}
              </h1>
              <p className="text-sm text-slate-500">
                {selectedSubject === 'stem'
                  ? 'AI-powered learning for Year 9-10 & GCSE'
                  : selectedCategory 
                    ? 'Select a topic to study' 
                    : selectedSubject 
                      ? 'Select a category'
                      : 'Choose a subject to start revision'}
              </p>
            </div>
            {selectedSubject === 'stem' && (
              <Badge className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-600 border-cyan-500/30">
                <Bot className="w-3 h-3 mr-1" /> AI Powered
              </Badge>
            )}
            {userProgress?.stats && !selectedSubject && (
              <Badge className="bg-violet-500/20 text-violet-600 border-violet-500/30">
                {userProgress.stats.completion_percentage}% Complete
              </Badge>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/welcome')}
                className="text-slate-900 hover:bg-slate-100"
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  localStorage.clear();
                  window.location.replace('/');
                }}
                className="text-slate-900 hover:bg-slate-100"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Subject Selection */}
        {!selectedSubject && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Info Banner */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-500/20 to-indigo-500/20 border border-violet-500/30">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-violet-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-slate-900">Learning Resources</h3>
                  <p className="text-sm text-slate-600">
                    {(() => {
                      // Stage-aware hero copy — was previously hardcoded to "11+ exam preparation"
                      // even when the logged-in user was Year 9 / GCSE etc.
                      const yg = userYearGroup;
                      if (/year ?(12|13)/.test(yg) || userType.includes('a-level') || userType.includes('alevel')) {
                        return 'A-Level resources — coming soon. Browse foundational content while we build your tier.';
                      }
                      if (/year ?(9|10|11)/.test(yg) || userType.includes('gcse') || userType.includes('ks4')) {
                        return 'Comprehensive resources for KS3 & GCSE — Maths, English and STEM topics curated for Year 9-11.';
                      }
                      if (/year ?(7|8)/.test(yg) || userType.includes('ks3')) {
                        return 'KS3 resources to build a strong foundation across Maths, English and Science.';
                      }
                      if (/year ?(5|6)/.test(yg) || userType.includes('11+') || userType.includes('eleven')) {
                        return 'Comprehensive resources for 11+ preparation.';
                      }
                      if (/year ?(1|2)/.test(yg) || userType.includes('7+') || userType.includes('seven')) {
                        return 'Comprehensive resources for 7+ exam preparation.';
                      }
                      return 'Resources spanning 7+ to GCSE — pick a subject to begin.';
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Subject Cards */}
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Choose a Subject</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Maths Card - KS2 (Year 4 & 5) - hidden for pure 7+ learners */}
              {showKS2MainCards && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="cursor-pointer bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-blue-500/30 hover:border-blue-400/50 transition-all"
                    onClick={() => setSelectedSubject('maths')}
                    data-testid="maths-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="maths" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">Maths</h3>
                          <p className="text-sm text-slate-600">9 categories</p>
                          <p className="text-xs text-slate-500 mt-1">Year 4 & 5</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* English Card - KS2 (Year 4/5/6) - hidden for pure 7+ learners */}
              {showKS2MainCards && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="cursor-pointer bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30 hover:border-emerald-400/50 transition-all"
                    onClick={() => setSelectedSubject('english')}
                    data-testid="english-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="english" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">English</h3>
                          <p className="text-sm text-slate-600">7 categories</p>
                          <p className="text-xs text-slate-500 mt-1">Year 4, 5 & 6</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Maths 7+ Card - Year 1/2 or admin */}
              {canAccess7Plus && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card
                    className="cursor-pointer bg-gradient-to-br from-sky-500/20 to-blue-500/20 border-sky-500/30 hover:border-sky-400/50 transition-all"
                    onClick={() => setSelectedSubject('maths-7plus')}
                    data-testid="maths-7plus-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="maths" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">Maths (7+)</h3>
                          <p className="text-sm text-slate-600">4 categories</p>
                          <p className="text-xs text-slate-500 mt-1">Year 1 & 2 (KS1)</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* English 7+ Card - Year 1/2 or admin */}
              {canAccess7Plus && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card
                    className="cursor-pointer bg-gradient-to-br from-rose-500/20 to-pink-500/20 border-rose-500/30 hover:border-rose-400/50 transition-all"
                    onClick={() => setSelectedSubject('english-7plus')}
                    data-testid="english-7plus-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="english" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">English (7+)</h3>
                          <p className="text-sm text-slate-600">4 categories</p>
                          <p className="text-xs text-slate-500 mt-1">Year 1 & 2 (KS1)</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Kumon-Style Practice 7+ Card - Year 1/2 or admin */}
              {canAccess7Plus && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card
                    className="cursor-pointer bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30 hover:border-amber-400/50 transition-all relative overflow-hidden"
                    onClick={() => setSelectedSubject('kumon-7plus')}
                    data-testid="kumon-7plus-subject-card"
                  >
                    <div className="absolute top-2 right-2">
                      <span className="text-[10px] font-bold uppercase bg-amber-500/90 text-white px-2 py-0.5 rounded-full">
                        Daily Drill
                      </span>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="maths" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">Kumon-Style Practice</h3>
                          <p className="text-sm text-slate-600">Maths + English drills</p>
                          <p className="text-xs text-slate-500 mt-1">Mastery • Speed • Self-learning</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Physics Card - Year 8+ */}
              {canAccessPhysics && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="cursor-pointer bg-gradient-to-br from-violet-500/20 to-purple-500/20 border-violet-500/30 hover:border-violet-400/50 transition-all"
                    onClick={() => setSelectedSubject('physics')}
                    data-testid="physics-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="physics" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">Physics</h3>
                          <p className="text-sm text-slate-600">4 categories</p>
                          <p className="text-xs text-slate-500 mt-1">Year 8 & 9 (KS3)</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* STEM Library Card - Year 9-10 / GCSE only */}
              {canAccessSTEM && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="cursor-pointer bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30 hover:border-cyan-400/50 transition-all"
                    onClick={() => setSelectedSubject('stem')}
                    data-testid="stem-subject-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <SubjectThumbnail subject="science" size="md" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900">STEM Library</h3>
                          <p className="text-sm text-slate-600">Maths, Physics, Chemistry, Biology</p>
                          <p className="text-xs text-slate-500 mt-1">Year 9-10 & GCSE</p>
                        </div>
                        <Badge className="bg-cyan-500/20 text-cyan-600 border-cyan-500/30 text-xs">
                          <Bot className="w-3 h-3 mr-1" /> AI
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* STEM Library View */}
        {selectedSubject === 'stem' && (
          <div className="space-y-6">
            {/* STEM Subject Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {stemTopics && Object.entries(stemTopics).map(([key, subject]) => {
                const Icon = STEM_SUBJECT_ICONS[key];
                const isActive = selectedStemSubject === key;
                const subjectColors = STEM_SUBJECT_COLORS[key];
                
                return (
                  <Button
                    key={key}
                    variant={isActive ? "default" : "ghost"}
                    className={`flex-shrink-0 gap-2 transition-all ${
                      isActive 
                        ? `bg-gradient-to-r ${subjectColors.bg} text-white shadow-lg` 
                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                    onClick={() => {
                      setSelectedStemSubject(key);
                      setSelectedStemCategory(null);
                      setSelectedStemTopic(null);
                      setGeneratedContent(null);
                    }}
                    data-testid={`stem-tab-${key}`}
                  >
                    <Icon className="h-4 w-4" />
                    {subject.name}
                  </Button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Topics Sidebar */}
              <div className="lg:col-span-4">
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Topics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
                    {stemSubjectData?.categories && Object.entries(stemSubjectData.categories).map(([category, topicList]) => (
                      <div key={category} className="space-y-1">
                        <button
                          className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-all ${
                            selectedStemCategory === category 
                              ? `${stemColors.badge} ${stemColors.text}` 
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                          }`}
                          onClick={() => setSelectedStemCategory(selectedStemCategory === category ? null : category)}
                        >
                          <span className="font-medium text-sm">{category}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${selectedStemCategory === category ? 'rotate-180' : ''}`} />
                        </button>
                        
                        <AnimatePresence>
                          {selectedStemCategory === category && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-3 space-y-1 py-1">
                                {topicList.map((topic) => (
                                  <button
                                    key={topic}
                                    className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                                      selectedStemTopic === topic 
                                        ? `${stemColors.badge} ${stemColors.text} font-medium` 
                                        : 'text-slate-500 hover:text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => {
                                      setSelectedStemTopic(topic);
                                      setCustomTopic('');
                                      setGeneratedContent(null);
                                    }}
                                  >
                                    {topic}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}

                    {/* Custom Topic Input */}
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Or enter custom topic:</p>
                      <Input
                        placeholder="Type any topic..."
                        value={customTopic}
                        onChange={(e) => {
                          setCustomTopic(e.target.value);
                          setSelectedStemTopic(null);
                        }}
                        className="bg-slate-100 border-slate-200 text-slate-900 text-sm placeholder:text-slate-600"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Content Area */}
              <div className="lg:col-span-8">
                {!selectedStemTopic && !customTopic ? (
                  <Card className="bg-white border-slate-200">
                    <CardContent className="p-8 text-center">
                      <Brain className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">Select a Topic</h3>
                      <p className="text-slate-500">
                        Choose a topic from the sidebar or enter your own to generate AI-powered learning content.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Topic Header */}
                    <Card className="bg-white border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div>
                            <Badge className={`${stemColors.badge} ${stemColors.text} mb-2`}>
                              {stemSubjectData?.name}
                            </Badge>
                            <h2 className="text-xl font-bold text-slate-900">
                              {selectedStemTopic || customTopic}
                            </h2>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Content Type Buttons */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { type: 'description', label: 'Explanation', icon: BookOpen },
                        { type: 'worked_example', label: 'Worked Example', icon: Lightbulb },
                        { type: 'common_mistakes', label: 'Common Mistakes', icon: HelpCircle },
                        { type: 'questions', label: 'Practice Questions', icon: Brain }
                      ].map(({ type, label, icon: Icon }) => (
                        <Button
                          key={type}
                          variant="outline"
                          className={`h-auto py-3 flex-col gap-2 ${
                            contentType === type && generatedContent
                              ? `${stemColors.badge} ${stemColors.text} border-current`
                              : 'border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                          }`}
                          onClick={() => generateStemContent(type)}
                          disabled={contentLoading}
                        >
                          <Icon className="h-5 w-5" />
                          <span className="text-xs">{label}</span>
                        </Button>
                      ))}
                    </div>

                    {/* Generated Content */}
                    {contentLoading ? (
                      <Card className="bg-white border-slate-200">
                        <CardContent className="p-8 text-center">
                          <Loader2 className="w-8 h-8 text-cyan-600 mx-auto mb-4 animate-spin" />
                          <p className="text-slate-500">Generating content with AI...</p>
                        </CardContent>
                      </Card>
                    ) : generatedContent ? (
                      <Card className="bg-white border-gray-200 shadow-lg">
                        <CardContent className="p-6">
                          <div className="prose max-w-none">
                            <div className="text-gray-800 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                              {generatedContent}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="bg-white border-slate-200 border-dashed">
                        <CardContent className="p-8 text-center">
                          <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                          <p className="text-slate-500">
                            Click a button above to generate AI content for this topic
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Standard Category Selection (Maths, English, Physics) */}
        {selectedSubject && selectedSubject !== 'stem' && !selectedCategory && (
          <>
            {/* Info Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-500/20 to-indigo-500/20 border border-violet-500/30"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-violet-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Quick Revision Topics</h3>
                  <p className="text-sm text-slate-600">
                    Each topic includes: Concept Explained, Worked Example, Quick Practice (5 Questions), 
                    Common Mistakes, and an 11+ Twist Question.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Categories Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => {
                  const IconComponent = CATEGORY_ICONS[category.id] || BookOpen;
                  const colorClass = CATEGORY_COLORS[category.color] || CATEGORY_COLORS.blue;
                  const bgClass = CATEGORY_BG[category.color] || CATEGORY_BG.blue;
                  const progressPercent = getCategoryProgress(category.id);

                  return (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card
                        className="bg-white border-slate-200 hover:border-slate-200 cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                        onClick={() => fetchCategoryTopics(category.id)}
                        data-testid={`category-${category.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${bgClass}`}>
                              <IconComponent className={`w-6 h-6 bg-gradient-to-r ${colorClass} bg-clip-text text-transparent`} style={{ color: category.color === 'blue' ? '#3b82f6' : category.color === 'green' ? '#22c55e' : category.color === 'purple' ? '#a855f7' : category.color === 'orange' ? '#f97316' : category.color === 'pink' ? '#ec4899' : '#14b8a6' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-900 mb-1 truncate">
                                {category.name}
                              </h3>
                              <p className="text-sm text-slate-500 mb-3">
                                {category.topic_count} topics
                              </p>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-slate-500">Progress</span>
                                  <span className="text-slate-500">{progressPercent}%</span>
                                </div>
                                <Progress value={progressPercent} className="h-1.5 bg-slate-50" />
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Topics List for standard subjects */}
        {selectedSubject && selectedSubject !== 'stem' && selectedCategory && (
          <div className="space-y-3">
            {categoryTopics?.topics?.map((topic, index) => {
              const progressPercent = getTopicProgress(selectedCategory, topic.id);
              const isComplete = progressPercent === 100;

              return (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`bg-white border-slate-200 hover:border-slate-200 cursor-pointer transition-all duration-300 ${isComplete ? 'border-green-500/30' : ''}`}
                    onClick={() => navigate(`/resources/${selectedSubject}/${selectedCategory}/${topic.id}`)}
                    data-testid={`topic-${topic.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isComplete ? 'bg-green-500/20' : 'bg-violet-500/20'}`}>
                          {isComplete ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <span className="text-violet-600 font-bold">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 truncate">{topic.title}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> 5 min read
                            </span>
                            <span className="text-xs text-slate-500">5 sections</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-16">
                            <Progress value={progressPercent} className="h-1.5 bg-slate-50" />
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceHub;
